import { useState, useCallback, useMemo } from "react";
import { message } from "antd";
import AppContext from "./AppContext";
import { SIDER_TABS } from "../utils/Constants";
import { AppStrings } from "../utils/constants/AppStrings";
import useAdminAuth from "../hooks/useAdminAuth";
import useTokenRefresh from "../hooks/useTokenRefresh";

const AppProvider = ({ children }) => {
  // Auth state
  const [token, setToken] = useState(() => {
    const stored = localStorage.getItem("token");
    return stored ? JSON.parse(stored) : {};
  });
  const [lastLogInTimestamp, setLastLogInTimestamp] = useState();

  // Admin data (delegated to hook)
  const {
    adminType,
    adminPermissions,
    adminActions,
    adminReadOnly,
    adminRegulatorManager,
    adminDataLoaded,
    adminTimezone,
    adminLanguage,
    adminUsername,
    adminEmail,
    adminRoleLabel,
  } =
    useAdminAuth(token, lastLogInTimestamp);

  // Proactive token refresh on visibility change
  useTokenRefresh({
    onRefreshSuccess: () => {
      message.info(AppStrings.PAM_SESSION_REFRESHED, 2);
    },
  });

  // UI state - Sidebar
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedSiderTab, setSelectedSiderTab] = useState(SIDER_TABS?.USERS);

  // UI state - Dropdowns
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [selectedDropdownOption, setSelectedDropdownOption] = useState();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [whichDropdownOpen, setWhichDropdownOpen] = useState([]);

  /**
   * Set the active sidebar tab and optionally show a dropdown option
   * @param {string} tabName
   * @param {string} dropdownOption
   */
  const setActiveSidebarTab = useCallback((tabName, dropdownOption = false) => {
    setSelectedSiderTab(tabName);

    if (dropdownOption) {
      setIsDropdownVisible(true);
      setSelectedDropdownOption(dropdownOption);
    }
  }, []);

  const handleWhichDropdownOpen = (tabName) => {
    setWhichDropdownOpen((prev) => {
      if (prev.includes(tabName)) {
        return prev.filter((item) => item !== tabName);
      } else {
        return [...prev, tabName];
      }
    });
  };

  const clearWhichDropdownOpen = useCallback(() => {
    setWhichDropdownOpen([]);
  }, []);

  const contextValue = useMemo(
    () => ({
      token,
      setToken,
      lastLogInTimestamp,
      setLastLogInTimestamp,
      isExpanded,
      setIsExpanded,
      selectedSiderTab,
      setSelectedSiderTab,
      selectedDropdownOption,
      setSelectedDropdownOption,
      isDropdownVisible,
      setIsDropdownVisible,
      setActiveSidebarTab,
      isDropdownOpen,
      setIsDropdownOpen,
      whichDropdownOpen,
      setWhichDropdownOpen,
      handleWhichDropdownOpen,
      clearWhichDropdownOpen,
      adminType,
      adminDataLoaded,
      adminPermissions,
      adminActions,
      adminReadOnly,
      adminRegulatorManager,
      adminTimezone,
      adminLanguage,
      adminUsername,
      adminEmail,
      adminRoleLabel,
    }),
    [
      token,
      lastLogInTimestamp,
      isExpanded,
      selectedSiderTab,
      selectedDropdownOption,
      isDropdownVisible,
      setActiveSidebarTab,
      isDropdownOpen,
      whichDropdownOpen,
      handleWhichDropdownOpen,
      clearWhichDropdownOpen,
      adminType,
      adminPermissions,
      adminActions,
      adminReadOnly,
      adminRegulatorManager,
      adminDataLoaded,
      adminTimezone,
      adminLanguage,
      adminUsername,
      adminEmail,
      adminRoleLabel,
    ]
  );

  return (
    <AppContext.Provider
      value={contextValue}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
